import React from 'react';
import PropTypes from 'prop-types';
import Button from './Button';
import { FaSearch, FaTimes } from 'react-icons/fa';

/**
 * Componente para barra de filtros y búsqueda
 * Adaptado al estilo de la aplicación de taller
 */
const FilterBar = ({
    onSearch,
    searchValue = '',
    searchPlaceholder = 'Buscar...',
    onReset,
    children,
    checkboxFilters = null,
    className = '',
    compact = false,
    showSearchIcon = true,
    showResetButton = true,
    containerClassName = '',
    resetButtonLabel = 'Limpiar filtros'
}) => {
    // Manejar la búsqueda de forma directa sin debounce
    const handleSearchChange = (e) => {
        const value = e.target.value;
        if (onSearch) {
            // Llamar directamente a onSearch para una respuesta inmediata
            onSearch(value);
        }
    };
    
    // Manejar la búsqueda cuando se presiona Enter
    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && onSearch) {
            onSearch(e.target.value);
        }
    };

    // Manejar el reset
    const handleReset = () => {
        if (onReset) {
            onReset();
        }
    };

    // Determinar si el input de búsqueda es el único elemento
    const isSearchOnly = !children;

    return (
        <div className={`mb-4 bg-gray-800 rounded-lg p-4 ${containerClassName}`}>
            <div className="flex flex-wrap gap-4">
                {/* Campo de búsqueda con estilo similar al mantenedor de repuestos */}
                {onSearch && (
                    <div className="flex-1 min-w-[200px]">
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <FaSearch className="text-gray-400" />
                            </div>
                            <input
                                type="text"
                                className="w-full pl-10 p-2 h-10 bg-gray-700 border border-gray-600 rounded-lg text-white"
                                placeholder={searchPlaceholder}
                                value={searchValue}
                                onChange={handleSearchChange}
                                onKeyDown={handleKeyDown}
                            />
                        </div>
                    </div>
                )}
                
                {/* Filtros adicionales (como el dropdown de categorías) */}
                {children && (
                    <div className="w-full md:w-auto">
                        {children}
                    </div>
                )}
                
                {/* Filtros de checkbox al mismo nivel */}
                {checkboxFilters && (
                    <div className="w-full md:w-auto flex justify-center md:justify-start gap-4">
                        {checkboxFilters}
                    </div>
                )}
                
                {/* Botón para limpiar filtros */}
                {showResetButton && onReset && (
                    <div className="w-full md:w-auto">
                        <button
                            onClick={handleReset}
                            className="p-2 h-10 bg-gray-700 border border-gray-600 rounded-lg text-white hover:bg-gray-600 w-full"
                        >
                            Limpiar filtros
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

FilterBar.propTypes = {
    /** Función para manejar cambios en la búsqueda */
    onSearch: PropTypes.func,
    /** Valor actual del campo de búsqueda */
    searchValue: PropTypes.string,
    /** Placeholder para el campo de búsqueda */
    searchPlaceholder: PropTypes.string,
    /** Función para resetear los filtros */
    onReset: PropTypes.func,
    /** Elementos de filtro adicionales */
    children: PropTypes.node,
    /** Filtros de checkbox que van al mismo nivel que search y reset */
    checkboxFilters: PropTypes.node,
    /** Clases CSS adicionales para el contenedor de filtros */
    className: PropTypes.string,
    /** Si debe mostrarse en modo compacto (ocupando menos espacio) */
    compact: PropTypes.bool,
    /** Si debe mostrar el icono de búsqueda */
    showSearchIcon: PropTypes.bool,
    /** Si debe mostrar el botón de reset */
    showResetButton: PropTypes.bool,
    /** Clases CSS adicionales para el contenedor principal */
    containerClassName: PropTypes.string,
    /** Texto para el botón de reset */
    resetButtonLabel: PropTypes.string
};

export default FilterBar;
